import { ApiClient } from "@ucl/library";

/**
 * A specialized ApiClient that:
 * 1. Normalizes the provided base URL to just its scheme and host (removing any path, query, or fragment).
 * 2. Appends "/bolt" to the resulting URL.
 * 3. Allows adding custom headers for every request.
 */
export class BoltApiClient extends ApiClient {
  /**
   * Creates a new instance of BoltApiClient.
   *
   * @param {string} baseUrl - The base URL or domain used for all requests. May include a path or query initially, but they will be stripped.
   * @param {Record<string, string>} [headers={}] - An optional object of key/value pairs to set as headers on every request.
   */
  constructor(
    baseUrl: string,
    headers: Record<string, string> = {},
    getHeaders: () => Promise<Record<string, string>> = () =>
      Promise.resolve({})
  ) {
    const url = new URL(baseUrl);

    // Remove any existing path, query, and fragment
    url.pathname = "";
    url.search = "";
    url.hash = "";

    // Append "/bolt" as the path
    url.pathname = "bolt";

    // Pass the configured URL and header-setting function to the parent ApiClient constructor
    super(url.toString(), async (config) => {
      Object.keys(headers).forEach((headerName) => {
        config.headers.set(headerName, headers[headerName]);
      });
      getHeaders().then((headers) => {
        Object.keys(headers).forEach((headerName) => {
          config.headers.set(headerName, headers[headerName]);
        });
      });
      return config;
    });
  }
}

/**
 * A convenience function for creating a BoltApiClient.
 *
 * @param {string} baseUrl - The base URL or domain used for all requests. May include a path or query initially, but they will be stripped.
 * @param {Record<string, string>} [headers={}] - An optional object of key/value pairs to set as headers on every request.
 * @returns {BoltApiClient} An instance of BoltApiClient configured with the supplied parameters.
 */
export const createBoltApiClient = (
  baseUrl: string,
  headers: Record<string, string> = {},
  getHeaders: () => Promise<Record<string, string>> = () => Promise.resolve({})
): BoltApiClient => {
  return new BoltApiClient(baseUrl, headers, getHeaders);
};

// Example usage:
/*
const boltApiClient = createBoltApiClient("https://api.bolt.com/some/deep/path?foo=bar", {
  Authorization: "Bearer <YourToken>",
});

boltApiClient.get("/communication/templates").then(response => {
  console.log(response.data);
});
*/
